// src/components/sales/client-interactions/ClientInteractionsView.jsx
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  Plus,
  RefreshCw,
  Pencil,
  Trash2,
  Filter,
  X,
  MessageSquare,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";

function formatDate(iso) {
  if (!iso) return "-";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString();
  } catch {
    return iso;
  }
}

export default function ClientInteractionsView({
  loading,
  clients,
  interactions,
  clientMap,
  clientIdFilter,
  setClientIdFilter,
  onApplyFilter,
  onClearFilter,
  onRefresh,
  onCreateClick,
  onEdit,
  onDelete,

  // ✅ Pagination props
  totalCount = 0,
  page = 1,
  pageSize = 5,
  totalPages = 1,
  onPageChange,
}) {
  const { t, i18n } = useTranslation();
  void i18n;

  const rows = Array.isArray(interactions) ? interactions : [];

  const headers = useMemo(
    () => [
      t("sales.clientInteractions.table.client"),
      t("sales.clientInteractions.table.type"),
      t("sales.clientInteractions.table.clientRequest"),
      t("sales.clientInteractions.table.report"),
      t("sales.clientInteractions.table.date"),
      t("sales.clientInteractions.table.actions"),
    ],
    [t]
  );

  // ✅ Pagination display helpers
  const safeTotal = Number.isFinite(totalCount) ? totalCount : rows.length;
  const safePageSize = Number.isFinite(pageSize) ? pageSize : 10;
  const safePage = Number.isFinite(page) ? page : 1;
  const tp = Math.max(1, Number(totalPages) || 1);

  const startIndex = safeTotal === 0 ? 0 : (safePage - 1) * safePageSize + 1;
  const endIndex = Math.min(safeTotal, safePage * safePageSize);

  const pageButtons = useMemo(() => {
    const windowSize = 5;
    let start = Math.max(1, safePage - Math.floor(windowSize / 2));
    let end = start + windowSize - 1;
    if (end > tp) {
      end = tp;
      start = Math.max(1, end - windowSize + 1);
    }
    const arr = [];
    for (let i = start; i <= end; i++) arr.push(i);
    return arr;
  }, [safePage, tp]);

  return (
    <div className="text-white">
      {/* Header */}
      <div className="mb-6 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className="p-2 rounded-xl bg-white/10 border border-white/20 backdrop-blur-xl">
            <MessageSquare className="w-6 h-6 text-pink-200" />
          </div>
          <div>
            <h2 className="text-xl font-bold">{t("sales.clientInteractions.title")}</h2>
            <p className="text-xs text-white/70">{t("sales.clientInteractions.subtitle")}</p>
          </div>
        </div>

        <div className="flex flex-wrap gap-3">
          <button
            onClick={onCreateClick}
            disabled={loading}
            className="px-4 py-2 rounded-xl bg-purple-600/80 hover:bg-purple-700 text-white flex items-center gap-2 backdrop-blur-lg disabled:opacity-50"
          >
            <Plus className="w-5 h-5" />
            {t("sales.clientInteractions.actions.add")}
          </button>

          <button
            onClick={onRefresh}
            className="px-4 py-2 rounded-xl bg-white/10 hover:bg-white/15 border border-white/20 text-white flex items-center gap-2 backdrop-blur-lg disabled:opacity-50"
            disabled={loading}
          >
            <RefreshCw className={`w-5 h-5 ${loading ? "animate-spin" : ""}`} />
            {t("common.refresh")}
          </button>
        </div>
      </div>

      {/* Filter Card */}
      <div className="mb-6 backdrop-blur-xl bg-white/10 rounded-2xl border border-white/20 shadow-[0_0_40px_rgba(255,0,255,0.15)] p-5">
        <div className="flex flex-col md:flex-row md:items-end gap-4">
          <div className="flex-1">
            <label className="text-sm text-gray-200">
              {t("sales.clientInteractions.filter.label")}
            </label>
            <select
              value={clientIdFilter}
              onChange={(e) => setClientIdFilter(e.target.value)}
              disabled={loading}
              className="mt-2 w-full px-3 py-2 bg-white/10 text-white border border-white/20 rounded-xl outline-none focus:ring-2 focus:ring-pink-400/30 disabled:opacity-50"
            >
              <option value="" className="text-black">
                {t("sales.clientInteractions.filter.allClients")}
              </option>
              {(clients || []).map((c) => (
                <option key={c.id} value={String(c.id)} className="text-black">
                  {c.companyName || t("sales.clientInteractions.clientFallback")} — #{c.id}
                </option>
              ))}
            </select>
          </div>

          <div className="flex gap-3">
            <button
              onClick={onApplyFilter}
              disabled={loading}
              className="px-4 py-2 rounded-xl bg-white/10 hover:bg-white/15 border border-white/20 text-white flex items-center gap-2 backdrop-blur-lg disabled:opacity-50"
            >
              <Filter className="w-5 h-5" />
              {t("sales.clientInteractions.actions.apply")}
            </button>

            <button
              onClick={onClearFilter}
              disabled={loading}
              className="px-4 py-2 rounded-xl bg-white/10 hover:bg-white/15 border border-white/20 text-white flex items-center gap-2 backdrop-blur-lg disabled:opacity-50"
            >
              <X className="w-5 h-5" />
              {t("sales.clientInteractions.actions.clear")}
            </button>
          </div>
        </div>
      </div>

      {/* Table Card */}
      <div className="backdrop-blur-xl bg-white/10 rounded-2xl shadow-[0_0_40px_rgba(255,0,255,0.20)] border border-white/20 overflow-hidden">
        <div className="p-6 bg-gradient-to-r from-pink-500/20 to-purple-600/30 backdrop-blur-xl flex items-center justify-between">
          <h3 className="text-lg font-bold">{t("sales.clientInteractions.listTitle")}</h3>
          <span className="text-sm text-gray-200">
            {t("sales.clientInteractions.totalCount", { total: safeTotal })}
          </span>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-white">
            <thead className="bg-white/10">
              <tr>
                {headers.map((h) => (
                  <th key={h} className="px-4 py-3 text-right text-sm font-semibold">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody className="divide-y divide-white/10">
              {rows.map((x) => {
                const client = clientMap?.get(String(x.clientId));
                return (
                  <tr key={x.id} className="hover:bg-white/10 transition">
                    <td className="px-4 py-3 text-right">
                      <div className="font-semibold">
                        {client?.companyName ||
                          t("sales.clientInteractions.clientNumber", { id: x.clientId ?? "-" })}
                      </div>
                      <div className="text-xs text-gray-300">
                        {client?.emailAddress || client?.phoneNumber || ""}
                      </div>
                    </td>

                    <td className="px-4 py-3 text-right">
                      <span className="px-2 py-1 rounded text-xs bg-white/10 border border-white/20">
                        {x.interactionType || "-"}
                      </span>
                    </td>

                    <td className="px-4 py-3 text-right">
                      <div className="text-sm whitespace-pre-wrap break-words max-w-[420px]">
                        {x.clientRequest || "-"}
                      </div>
                    </td>

                    <td className="px-4 py-3 text-right">
                      <div className="text-sm whitespace-pre-wrap break-words max-w-[420px]">
                        {x.report || "-"}
                      </div>
                    </td>

                    <td className="px-4 py-3 text-right">
                      <div className="text-sm">{formatDate(x.interactionDate)}</div>
                    </td>

                    <td className="px-4 py-3 text-right">
                      <div className="flex gap-2 justify-end">
                        <button
                          onClick={() => onEdit?.(x)}
                          disabled={loading}
                          className="px-3 py-1.5 rounded-lg bg-pink-600/80 hover:bg-pink-700 text-white text-xs backdrop-blur-lg flex items-center gap-1 disabled:opacity-50"
                        >
                          <Pencil className="w-4 h-4" />
                          {t("common.edit")}
                        </button>

                        <button
                          onClick={() => onDelete?.(x.id)}
                          disabled={loading}
                          className="px-3 py-1.5 rounded-lg bg-red-500/20 hover:bg-red-500/30 border border-red-400/30 text-red-100 text-xs backdrop-blur-lg flex items-center gap-1 disabled:opacity-50"
                        >
                          <Trash2 className="w-4 h-4" />
                          {t("common.delete")}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}

              {!loading && rows.length === 0 && (
                <tr>
                  <td className="px-4 py-10 text-center text-gray-200" colSpan={6}>
                    {t("sales.clientInteractions.empty")}
                  </td>
                </tr>
              )}

              {loading && (
                <tr>
                  <td className="px-4 py-10 text-center text-gray-200" colSpan={6}>
                    {t("common.loading")}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* ✅ Pagination Footer */}
       <div className="flex flex-col md:flex-row gap-3 md:items-center md:justify-between px-4 py-4 border-t border-white/10 bg-white/5">
  <div className="text-xs text-white/70">
    {safeTotal === 0 ? (
      "—"
    ) : (
      <span>
        {t("sales.clientInteractions.pagination.showingRange", {
          start: startIndex,
          end: endIndex,
          total: safeTotal,
        })}
      </span>
    )}
  </div>

  <div className="flex items-center gap-2 justify-end">
    <button
      type="button"
      onClick={() => onPageChange?.(safePage - 1)}
      disabled={loading || safePage <= 1}
      className="px-3 py-2 rounded-xl bg-white/10 hover:bg-white/15 border border-white/20 text-white disabled:opacity-50 flex items-center gap-2"
    >
      <ChevronRight className="w-4 h-4" />
      {t("sales.clientInteractions.pagination.prev")}
    </button>

    <div className="flex items-center gap-1">
      {pageButtons.map((p) => (
        <button
          key={p}
          type="button"
          onClick={() => onPageChange?.(p)}
          disabled={loading}
          className={[
            "min-w-[40px] px-3 py-2 rounded-xl border text-sm",
            p === safePage
              ? "bg-purple-600/60 border-purple-300/30 text-white"
              : "bg-white/10 hover:bg-white/15 border-white/20 text-white",
            loading ? "opacity-50" : "",
          ].join(" ")}
          aria-current={p === safePage ? "page" : undefined}
          title={t("sales.clientInteractions.pagination.pageOf", { page: p, pages: tp })}
        >
          {p}
        </button>
      ))}
    </div>

    <button
      type="button"
      onClick={() => onPageChange?.(safePage + 1)}
      disabled={loading || safePage >= tp}
      className="px-3 py-2 rounded-xl bg-white/10 hover:bg-white/15 border border-white/20 text-white disabled:opacity-50 flex items-center gap-2"
    >
      {t("sales.clientInteractions.pagination.next")}
      <ChevronLeft className="w-4 h-4" />
    </button>
  </div>
</div>

      </div>
    </div>
  );
}
